import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ClientTableModel } from 'src/app/models/archive/client-table-model';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { ImportClientComponent } from '../import-client/import-client.component';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
  standalone: false
})
export class ClientComponent implements OnInit {

  @ViewChild('clientImportModal') clientImportModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  componentClientImportModal = ImportClientComponent;
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ClientTableModel> = new PagedResponseModel<ClientTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Clientes';
  page: number = 1;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  permissionUpdatable: boolean = false;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = "client";
  userAuthenticated: UserModel = new UserModel();

  constructor(private clientService: ClientService,
    private confirmDialogService: ConfirmDialogService,
    private headerService: HeaderService,
    private optionWindowService: OptionWindowService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private windowService: WindowService) {
    this.getPermission();
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getClientsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async deleteClient(client: ClientTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para eliminar clientes. Comuníquese con el administrador del sistema.',
      );
      return;
    }

    if (await this.confirmDialogService.confirm(`¿Realmente desea eliminar al cliente ${client.businessName}?`)) {
      try {
        await firstValueFrom(this.clientService.deleteClient(client.clientId));
        this.toastHandlerService.showToast(
          'success',
          'El cliente fue eliminado exitosamente.',
        );
        this.getClientsWithPagination();
      } catch (error) {
        console.error('Error al eliminar el cliente.', error);
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(
        this.clientService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant)
      );
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Clientes.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar clientes.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.clientService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getClientsWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.clientService.getAllClientsWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar los clientes.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionProfileByUrl(),
      this.getPermissionsForUserByWindow(),
    ]);
    this.getUserAuthenticated();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por perfil.', error);
    }
  }

  async getPermissionsForUserByWindow() {
    try {
      this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.Client));
    } catch (error) {
      console.error('Error al buscar los permisos por perfil.', error);
    }
  }

  async getUserAuthenticated() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0))
    );
  }

  newClient() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear clientes. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    this.router.navigate([`/${this.url}/register`]);
  }

  async onAction(event: { action: string, item?: ClientTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
    if (event.action === 'delete' && event.item) {
      await this.deleteClient(event.item);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onImport() {
    const hasImportPermission = this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador ||
      this.permissionsUser.find(x => x.code == OptionWindow.Importar);

    if (!hasImportPermission) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para importar Clientes, comuniquese con el administrador del sistema.'
      );
      return;
    }

    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para la edición de Clientes. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    this.clientImportModalComponent?.show();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getClientsWithPagination();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async reloadList(reload: boolean) {
    if (reload) {
      await this.getClientsWithPagination();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
